import { SelectListItemRenderer } from '@acies/core';
import { Label, theme } from '@acies/ui';
import type ISearchResult from './search-result';

/**
 * The renderer used in the List of the SearchAutoComplete popover.
 *
 * A result is presented by the code it is stored under, followed by what it is called. That is all {@link ISearchResult} promises, and it is what tells one code from another: the codes are the engine's own and read alike, so the name beside them is what a user actually picks by.
 */
export default class SearchItemRenderer<Item extends ISearchResult> extends SelectListItemRenderer<Item> {
	#keyLabel!: Label;
	#nameLabel!: Label;

	get #isPressed(): boolean {
		return this.styledState === 'pressed' || this.styledState === 'focusedPressed';
	}

	public constructor() {
		super();
		// the popover sits between the search field and the next field in the row, so tabbing on
		// would otherwise walk through every result found instead of leaving the search behind. The
		// arrow keys are the way in, and they focus a renderer outright rather than tab to it
		this.tabbable = false;
		this.width = 'fill';
		this.height = 32;
		this.alignment = 'left';
		this.cornerRadius = 4;
		this.cursor = 'pointer';
		this.gapHorizontal = 10;
		this.paddingHorizontal = 10;
	}

	public get keyLabel(): Label {
		if (!this.#keyLabel) {
			this.#keyLabel = new Label();
			this.#keyLabel.width = 256;
		}
		return this.#keyLabel;
	}

	public get nameLabel(): Label {
		if (!this.#nameLabel) {
			this.#nameLabel = new Label();
			this.#nameLabel.width = 'fill';
		}
		return this.#nameLabel;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.keyLabel, this.nameLabel]);
	}

	protected override itemChanged(): void {
		super.itemChanged();
		if (this.item) {
			this.keyLabel.text = this.item.key;
			this.nameLabel.text = this.item.name;
		} else {
			this.keyLabel.text = '';
			this.nameLabel.text = '';
		}
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		this.#updateIdleSelectedStyles();
	}

	/** Asks the styled state rather than the state, so hovering has no look of its own: a result under the pointer looks as it would without it, and a selected one still looks selected. */
	protected override stateChanged(): void {
		super.stateChanged();
		if (this.styledState === 'idle') {
			this.#setIdleStyles();
		} else if (this.styledState === 'focused') {
			this.#setFocusedStyles();
		} else if (this.#isPressed) {
			this.#setPressedStyles();
		}
	}

	#setFocusedStyles(): void {
		this.fill = theme.colors.fill.focus;
		this.stroke = theme.strokes.focus;
	}

	#setIdleStyles(): void {
		this.#updateIdleSelectedStyles();
	}

	#setPressedStyles(): void {
		this.fill = null;
		this.stroke = null;
	}

	#updateIdleSelectedStyles(): void {
		if (this.selected) {
			this.fill = theme.colors.fill.focus;
			this.stroke = null;
		} else {
			this.fill = null;
			this.stroke = null;
		}
	}
}
customElements.define('cf-search-item-renderer', SearchItemRenderer);
